/**
 * Gift with purchase. Ported from the Exercere theme (assets/free-gift.js).
 *
 * Shows the in-cart gift picker once the eligible subtotal reaches the threshold, and keeps the
 * cart in sync with the customer's choice.
 *
 * Rules:
 * - Not active for this visitor (switched off, or country not allowed): every gift line is removed.
 * - Below the threshold: the picker is hidden and the gift line is removed.
 * - At or above the threshold with no gift yet: the picker is shown. Nothing is added
 *   automatically; the customer picks a gift, then (Next) its size, then saves.
 * - Once a gift is in the cart the picker hides; the gift line is the confirmation. "Change gift"
 *   on the gift line reopens it.
 * - One gift at a time. Changing the choice removes the old line before adding the new one.
 * - Remove on the gift line, or × on the picker, declines the gift: any gift is removed and
 *   nothing is offered for 24 hours on this device (localStorage). "Undo" stays in the cart for
 *   10 seconds and puts back the gift, or the picker.
 * - While declined, the cart attribute free_gift_declined holds when the customer declined, so an
 *   order placed in those 24 hours shows it under Additional details.
 *
 * The picker (snippets/cart-gwp-picker.liquid) re-renders on every cart change and carries the
 * eligible subtotal, so this file only toggles visibility and makes cart changes. It refreshes the
 * cart with window.cart.getCart({ quiet: true }), which rebuilds the cart without opening a closed
 * drawer (see build() in theme.dev.js).
 *
 * Config: window.gwpConfig, set in layout/theme.liquid.
 * Currency: the threshold is in the primary currency, converted with window.Shopify.currency.rate.
 */
(function () {
  'use strict';

  const config = window.gwpConfig;
  if (!config) return;

  const GIFT_PROPERTY = '_is_free_gift';
  // Declining the gift (Remove or ×) lasts this long on this device; Undo is offered for 10 s.
  const DECLINE_KEY = 'gwp-declined-until';
  const DECLINE_MS = 24 * 60 * 60 * 1000;
  const UNDO_MS = 10 * 1000;
  // Cart attribute, carried over to the order's additional details: when the customer declined.
  const DECLINED_ATTRIBUTE = 'free_gift_declined';
  const JSON_HEADERS = {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  };

  const state = {
    busy: false,
    queued: null,
    evaluateTimeout: null,
    // Last threshold verdict, applied the moment the cart re-renders so the picker doesn't blink
    // out during the debounce.
    overThreshold: false,
    // Survives the re-render after a cart change, so the customer gets to read it.
    hint: '',
    // The gift the customer last clicked. Liquid can only pre-select the first gift while no
    // gift is in the cart, so without this the selection jumps back on every re-render.
    selectedProductId: '',
    // The gift variant picked but not saved yet; also survives a re-render.
    selectedVariantId: '',
    // The picker's step: 'gift' (the colour) or 'size'. Also survives a re-render.
    step: 'gift',
    // "Change gift" was clicked: keep the picker open although a gift is in the cart.
    changing: false,
    // The gift last added automatically, so a failed add (sold out meanwhile) is not retried on
    // every cart change. Cleared when the cart drops below the threshold.
    autoTried: '',
    // The picker is showing. A cart re-render swaps in a hidden copy, which must not replay the
    // entrance animation.
    pickerShown: false,
    // Declined until this time (ms). Kept in localStorage; this copy covers blocked storage.
    declinedUntil: 0,
    // The undo note shows until this time (ms), and Undo puts back this gift ('' = the picker).
    undoUntil: 0,
    undoVariantId: '',
    undoTimeout: null,
    // The decline value last sent to the cart, so a failed save isn't retried on every evaluation.
    attributeTried: null
  };

  // --- DOM helpers ---------------------------------------------------------

  function picker() {
    return document.querySelector('[data-free-gift-picker]');
  }

  /** The picker renders an empty shell when no gift can be offered (all sold out). */
  function canPick(el) {
    return !!el.querySelector('[data-gift-style-panel]');
  }

  /** Only available variants are rendered, so absence means sold out or withdrawn. */
  function variantIsOffered(el, variantId) {
    return !!el.querySelector('[data-gift-variant="' + variantId + '"]');
  }

  function setBusy(isBusy) {
    const el = picker();
    if (el) el.classList.toggle('is-busy', isBusy);
  }

  function setPickerVisible(visible) {
    const el = picker();
    if (!el) return;
    if (visible && canPick(el)) {
      el.removeAttribute('hidden');
      if (!state.pickerShown) callAttention(el, 'is-entering');
      state.pickerShown = true;
    } else {
      el.setAttribute('hidden', '');
      state.pickerShown = false;
    }
  }

  /** Plays one of the picker's attention animations (assets/gwp.css) from the start. */
  function callAttention(el, className) {
    el.classList.remove('is-entering', 'is-calling');
    void el.offsetWidth;
    el.classList.add(className);
  }

  function setHint(message) {
    state.hint = message || '';
    renderHint();
  }

  function renderHint() {
    const el = picker();
    if (!el) return;
    const hint = el.querySelector('[data-gift-hint]');
    if (!hint) return;
    if (state.hint) {
      hint.textContent = state.hint;
      hint.removeAttribute('hidden');
    } else {
      hint.setAttribute('hidden', '');
    }
  }

  /**
   * Restore the gift the customer clicked before the re-render, or make sure one gift is active
   * so image, title and size row are all present. Visual only.
   */
  function syncPanels() {
    const el = picker();
    if (!el) return;

    const panel = el.querySelector('[data-gift-style-panel]');
    if (!panel) return;

    preloadImages(el);

    const options = panel.querySelectorAll('.cart-gift-picker__products [data-gift-product]');
    let target = null;

    // With a gift in the cart, the server-rendered selection is the real one.
    if (!el.hasAttribute('data-gift-line-key') && state.selectedProductId) {
      for (let i = 0; i < options.length; i++) {
        if (options[i].getAttribute('data-gift-product') === state.selectedProductId) {
          target = options[i];
          break;
        }
      }
    }

    if (!target) {
      target = panel.querySelector('.cart-gift-picker__products [data-gift-product].is-active') || options[0];
    }
    if (!target) return;

    // Not the gift the customer was at (it sold out meanwhile): start again from the first step.
    if (target.getAttribute('data-gift-product') !== state.selectedProductId) state.step = 'gift';
    selectProduct(target);
  }

  function markActiveSize(row, size) {
    const sizes = row.querySelectorAll('[data-gift-variant]');
    for (let i = 0; i < sizes.length; i++) {
      sizes[i].classList.toggle('is-active', sizes[i] === size);
    }
  }

  /** Save is only possible once a gift and, if it has sizes, a size are picked. */
  function updateSave(panel) {
    const save = panel.querySelector('[data-gift-save]');
    if (save) save.disabled = !state.selectedVariantId;
  }

  function sizeRow(panel, productId) {
    return panel.querySelector('[data-gift-size-row="' + productId + '"]');
  }

  function setHidden(node, hidden) {
    if (node) node.toggleAttribute('hidden', hidden);
  }

  /**
   * Show one step: the gifts with Next, or the chosen gift's sizes with Back and Save. A gift
   * without sizes is saved from the first step; with only one gift (no gift step), its sizes are
   * the only step.
   */
  function renderStep(panel) {
    const giftStep = panel.querySelector('[data-gift-step]');
    const row = sizeRow(panel, state.selectedProductId);
    if (!row) state.step = 'gift';
    const onSizes = !!row && (!giftStep || state.step === 'size');

    setHidden(giftStep, onSizes);
    const rows = panel.querySelectorAll('[data-gift-size-row]');
    for (let i = 0; i < rows.length; i++) {
      setHidden(rows[i], !onSizes || rows[i] !== row);
    }

    // "Step 1 of 2" only when the chosen gift has sizes, "Step 2 of 2" only after a gift step.
    const progress = panel.querySelectorAll('[data-gift-progress]');
    for (let i = 0; i < progress.length; i++) {
      setHidden(progress[i], progress[i].closest('[data-gift-step]') ? !row : !giftStep);
    }

    setHidden(panel.querySelector('[data-gift-back]'), !onSizes || !giftStep);
    setHidden(panel.querySelector('[data-gift-next]'), onSizes || !row);
    setHidden(panel.querySelector('[data-gift-save]'), !onSizes && !!row);
    updateSave(panel);
  }

  /** Next and Back. The clicked button hides, so its focus moves to the chosen option. */
  function goToStep(button, step) {
    const panel = button.closest('[data-gift-style-panel]');
    if (!panel) return;

    const hadFocus = document.activeElement === button;
    state.step = step;
    renderStep(panel);
    if (!hadFocus) return;

    const row = sizeRow(panel, state.selectedProductId);
    const target =
      step === 'size' && row
        ? row.querySelector('[data-gift-variant].is-active') || row.querySelector('[data-gift-variant]')
        : panel.querySelector('.cart-gift-picker__products [data-gift-product].is-active');
    if (target) target.focus({ preventScroll: true });
  }

  /**
   * Mark a gift active: swap image and title, work out the picked variant (the gift's only
   * variant, or the size picked in its row) and show the current step. No cart change.
   */
  function selectProduct(option) {
    const panel = option.closest('[data-gift-style-panel]');
    if (!panel) return;

    const productId = option.getAttribute('data-gift-product');
    state.selectedProductId = productId;

    const options = panel.querySelectorAll('.cart-gift-picker__products [data-gift-product]');
    for (let i = 0; i < options.length; i++) {
      options[i].classList.toggle('is-active', options[i] === option);
    }

    const image = panel.querySelector('[data-gift-main-image]');
    const imageSrc = option.getAttribute('data-gift-image');
    if (image && imageSrc) {
      image.src = imageSrc;
      image.removeAttribute('hidden');
    }

    const title = panel.querySelector('[data-gift-main-title]');
    const titleText = option.getAttribute('data-gift-title');
    if (title && titleText) title.textContent = titleText;

    const row = sizeRow(panel, productId);

    // The size picked before a re-render wins over the server-rendered one (the gift in the cart).
    let variantId = option.getAttribute('data-gift-variant') || '';
    if (!variantId && row) {
      const remembered = state.selectedVariantId
        ? row.querySelector('[data-gift-variant="' + state.selectedVariantId + '"]:not(.is-disabled)')
        : null;
      const size = remembered || row.querySelector('[data-gift-variant].is-active');
      if (size) {
        markActiveSize(row, size);
        variantId = size.getAttribute('data-gift-variant');
      }
    }
    state.selectedVariantId = variantId;
    renderStep(panel);
  }

  /**
   * In the cart drawer on a wide screen, put the open terms dialog beside the drawer, level with
   * the link. Otherwise (cart page, phones) it stays centred by CSS.
   */
  function placeTerms(modal, link) {
    modal.style.top = modal.style.left = modal.style.right = modal.style.bottom = modal.style.margin = '';

    const drawer = link.closest('.drawer__inner');
    if (!drawer) return;

    const gap = 16;
    const area = drawer.getBoundingClientRect();
    const width = modal.offsetWidth;
    const height = modal.offsetHeight;
    if (area.left < width + gap * 2) return;

    const anchor = link.getBoundingClientRect();
    const level = anchor.top + anchor.height / 2 - height / 2;
    modal.style.margin = '0';
    modal.style.right = 'auto';
    modal.style.bottom = 'auto';
    modal.style.left = area.left - width - gap + 'px';
    modal.style.top = Math.max(gap, Math.min(level, window.innerHeight - height - gap)) + 'px';
  }

  /** Warm the gift images so switching between gifts is instant. */
  function preloadImages(el) {
    const options = el.querySelectorAll('.cart-gift-picker__products [data-gift-product]');
    for (let i = 0; i < options.length; i++) {
      const src = options[i].getAttribute('data-gift-image');
      if (src) new Image().src = src;
    }
  }

  // --- Cart API ------------------------------------------------------------

  function getCart() {
    return fetch(window.theme.routes.cart_url + '.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (res) {
      return res.json();
    });
  }

  function isGiftItem(item) {
    return !!(item.properties && item.properties[GIFT_PROPERTY] === 'true');
  }

  function giftsInCart(cart) {
    return cart.items.filter(isGiftItem);
  }

  /** Threshold in subunits (øre/cents) of the presentment currency, like the cart's prices. */
  function getThresholdInSubunits() {
    const rate = (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
    return Math.round(config.thresholdBase * rate * 100);
  }

  function addGift(variantId) {
    const properties = {};
    properties[GIFT_PROPERTY] = 'true';

    return fetch(window.theme.routes.cart_add_url + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: JSON_HEADERS,
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1, properties: properties }]
      })
    }).then(function (res) {
      // Sold out between render and click.
      return res.ok ? res.json() : null;
    });
  }

  /** Changes a line by its key, never by variant, so a paid line of the same product is untouched. */
  function changeLine(key, quantity) {
    return fetch(window.theme.routes.cart_change_url + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: JSON_HEADERS,
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(function (res) {
      return res.ok ? res.json() : null;
    });
  }

  /** Removes the given lines one after the other (each change shifts the cart). */
  function removeLines(items) {
    return items.reduce(function (chain, item) {
      return chain.then(function () {
        return changeLine(item.key, 0);
      });
    }, Promise.resolve());
  }

  function refreshCart() {
    if (window.cart && typeof window.cart.getCart === 'function') {
      window.cart.getCart({ quiet: true });
    }
  }

  // --- Mutation lock -------------------------------------------------------

  /**
   * One cart change at a time. A click during a change is queued, and only the last queued
   * selection is applied.
   */
  function withLock(fn) {
    if (state.busy) return Promise.resolve(null);
    state.busy = true;
    setBusy(true);

    return fn()
      .catch(function () {
        return null;
      })
      .then(function (result) {
        state.busy = false;
        setBusy(false);

        const queued = state.queued;
        state.queued = null;
        if (queued) selectGift(queued.variantId);

        return result;
      });
  }

  // --- Selection -----------------------------------------------------------

  function selectGift(variantId) {
    if (state.busy) {
      state.queued = { variantId: variantId };
      return;
    }

    withLock(function () {
      return getCart().then(function (cart) {
        const gifts = giftsInCart(cart);

        if (gifts.length === 1 && String(gifts[0].variant_id) === String(variantId)) return null;

        // Remove first, so two gift lines never exist at the same time.
        return removeLines(gifts)
          .then(function () {
            return addGift(variantId);
          })
          .then(function (result) {
            if (!result) {
              setHint(config.unavailableText);
              refreshCart();
              return null;
            }
            state.hint = '';
            state.changing = false;
            state.step = 'gift';
            setPickerVisible(false);
            refreshCart();
            return result;
          });
      });
    });
  }

  // --- Declining -----------------------------------------------------------

  function declinedUntil() {
    let until = state.declinedUntil;
    try {
      const stored = window.localStorage.getItem(DECLINE_KEY);
      if (stored !== null) until = Number(stored) || 0;
    } catch (error) {
      // Storage blocked: the copy in state lasts until the customer leaves the page.
    }
    return until;
  }

  function isDeclined() {
    return Date.now() < declinedUntil();
  }

  /**
   * The value free_gift_declined needs, or null when the cart has it already: when the customer
   * declined ("2026-10-08 12:32 UTC"), or '' to clear it. A missing and a blank value both mean
   * "not declined", so a cart that never declined is never written to.
   */
  function declinedAttributeNeeded(cart) {
    const until = declinedUntil();
    const wanted =
      Date.now() < until ? new Date(until - DECLINE_MS).toISOString().slice(0, 16).replace('T', ' ') + ' UTC' : '';
    const current = (cart.attributes && cart.attributes[DECLINED_ATTRIBUTE]) || '';
    return current === wanted || state.attributeTried === wanted ? null : wanted;
  }

  /** Cart attributes carry over to the order, where they show under Additional details. */
  function saveDeclinedAttribute(value) {
    state.attributeTried = value;
    const attributes = {};
    attributes[DECLINED_ATTRIBUTE] = value;
    return fetch(window.theme.routes.cart_update_url + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: JSON_HEADERS,
      body: JSON.stringify({ attributes: attributes })
    }).then(function (res) {
      return res.ok ? res.json() : null;
    });
  }

  /** Brings free_gift_declined in step with the decline, within the cart lock. */
  function syncDeclinedAttribute(cart) {
    const value = declinedAttributeNeeded(cart);
    return value === null ? Promise.resolve(null) : saveDeclinedAttribute(value);
  }

  function setDeclined(declined) {
    state.declinedUntil = declined ? Date.now() + DECLINE_MS : 0;
    try {
      if (declined) {
        window.localStorage.setItem(DECLINE_KEY, String(state.declinedUntil));
      } else {
        window.localStorage.removeItem(DECLINE_KEY);
      }
    } catch (error) {
      // See isDeclined().
    }
  }

  /** The cart re-renders the undo note hidden; it stays up for its 10 seconds. */
  function renderUndo() {
    const note = document.querySelector('[data-gift-undo]');
    if (note) note.toggleAttribute('hidden', Date.now() >= state.undoUntil);
  }

  function showUndo(variantId) {
    clearTimeout(state.undoTimeout);
    state.undoUntil = Date.now() + UNDO_MS;
    state.undoVariantId = variantId || '';
    state.undoTimeout = setTimeout(hideUndo, UNDO_MS);
    renderUndo();
  }

  function hideUndo() {
    clearTimeout(state.undoTimeout);
    state.undoUntil = 0;
    state.undoVariantId = '';
    renderUndo();
  }

  /**
   * Remove on the gift line and × on the picker: removes the gift, if there is one, and offers
   * nothing for 24 hours on this device. Undo puts it back for the next 10 seconds.
   */
  function declineGift() {
    const el = picker();
    const line = document.querySelector('.cart__item--free-gift[data-variant-id]');
    const variantId =
      (el && el.getAttribute('data-gift-variant-id')) || (line && line.getAttribute('data-variant-id')) || '';

    setDeclined(true);
    state.changing = false;
    state.step = 'gift';
    state.queued = null;
    state.hint = '';
    state.autoTried = '';
    setPickerVisible(false);
    showUndo(variantId);

    // Gone at once; the cart refresh below confirms it.
    const gone = document.querySelectorAll('.cart__item--free-gift, .gwp-terms');
    for (let i = 0; i < gone.length; i++) {
      gone[i].setAttribute('hidden', '');
    }

    // During another cart change the lock is taken; the evaluation after that change does this.
    withLock(function () {
      return getCart().then(function (cart) {
        const gifts = giftsInCart(cart);
        return removeLines(gifts)
          .then(function () {
            return syncDeclinedAttribute(cart);
          })
          .then(function () {
            if (gifts.length) refreshCart();
          });
      });
    });
  }

  /**
   * Undo: clear the decline on the cart, then offer the gift again, and put back the gift that was
   * removed, if there was one.
   */
  function undoDecline() {
    const variantId = state.undoVariantId;
    setDeclined(false);
    hideUndo();
    withLock(function () {
      return getCart().then(syncDeclinedAttribute);
    }).then(function () {
      if (variantId) {
        selectGift(variantId);
      } else {
        evaluateCart();
      }
    });
  }

  // --- Evaluation ----------------------------------------------------------

  function evaluateCart() {
    if (state.busy) return;

    getCart()
      .then(function (cart) {
        // The order shows a decline from the last 24 hours (free_gift_declined). One cart change at
        // a time: bring that in step first, then evaluate again.
        if (declinedAttributeNeeded(cart) !== null) {
          return withLock(function () {
            return syncDeclinedAttribute(cart);
          }).then(evaluateCart);
        }

        const gifts = giftsInCart(cart);

        // Switched off or not allowed in this country: no gift may stay in the cart.
        if (!config.active) {
          if (!gifts.length) return null;
          return withLock(function () {
            return removeLines(gifts).then(refreshCart);
          });
        }

        // The picker carries the eligible subtotal. Without it (no cart drawer on this page) the
        // cart is left alone; the drawer or cart page evaluates it when shown.
        const el = picker();
        if (!el) return null;

        const subtotal = Number(el.getAttribute('data-eligible-subtotal')) || 0;
        const overThreshold = subtotal >= getThresholdInSubunits();
        const gift = gifts[0] || null;
        state.overThreshold = overThreshold;

        // Declined in the last 24 hours: no picker, and no gift may stay or be added.
        if (isDeclined()) {
          setPickerVisible(false);
          if (!gifts.length) return null;
          return withLock(function () {
            return removeLines(gifts).then(refreshCart);
          });
        }

        if (!overThreshold) {
          setPickerVisible(false);
          state.autoTried = '';
          state.step = 'gift';
          if (!gifts.length) return null;
          return withLock(function () {
            return removeLines(gifts).then(refreshCart);
          });
        }

        if (!gift) state.changing = false;

        // Only one gift with one variant: nothing to choose, so it is added without the picker.
        const autoVariant = el.getAttribute('data-gift-auto-variant');
        if (autoVariant) {
          setPickerVisible(false);
          if (!gift && state.autoTried !== autoVariant) {
            state.autoTried = autoVariant;
            selectGift(autoVariant);
            return null;
          }
        } else {
          setPickerVisible(!gift || state.changing);
          syncPanels();
          renderHint();
        }

        // Only one gift, quantity one.
        if (gifts.length > 1) {
          return withLock(function () {
            return removeLines(gifts.slice(1)).then(refreshCart);
          });
        }

        if (gift && gift.quantity > 1) {
          return withLock(function () {
            return changeLine(gift.key, 1).then(refreshCart);
          });
        }

        // The chosen variant sold out or left the gift collection: remove it and say why.
        if (gift && !variantIsOffered(el, gift.variant_id)) {
          setHint(config.unavailableText);
          return withLock(function () {
            return changeLine(gift.key, 0).then(refreshCart);
          });
        }

        if (gift) setHint('');

        return null;
      })
      .catch(function () {
        // Never break the cart.
      });
  }

  function scheduleEvaluation() {
    if (state.evaluateTimeout) clearTimeout(state.evaluateTimeout);
    state.evaluateTimeout = setTimeout(evaluateCart, 500);
  }

  // --- Events --------------------------------------------------------------

  // Delegated on document so it survives the cart's innerHTML swap.
  document.addEventListener('click', function (event) {
    const el = picker();

    const change = event.target.closest('[data-free-gift-change]');
    if (change) {
      event.preventDefault();
      if (!el) return;
      state.changing = true;
      state.step = 'gift';
      setPickerVisible(true);
      syncPanels();
      renderHint();
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      return;
    }

    // Gift terms dialog (under the gift line, outside the picker). Escape closes it natively.
    const termsOpen = event.target.closest('[data-gwp-terms-open]');
    if (termsOpen) {
      event.preventDefault();
      const modal = termsOpen.parentElement.querySelector('[data-gwp-terms-modal]');
      if (modal && typeof modal.showModal === 'function') {
        modal.showModal();
        placeTerms(modal, termsOpen);
      }
      return;
    }

    const termsModal = event.target.closest('[data-gwp-terms-modal]');
    if (termsModal) {
      // The close button, or a click on the backdrop: that also targets the dialog itself, so
      // only a click outside its box counts (not one on its padding).
      const box = termsModal.getBoundingClientRect();
      const onBackdrop =
        event.target === termsModal &&
        (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom);
      if (event.target.closest('[data-gwp-terms-close]') || onBackdrop) {
        event.preventDefault();
        termsModal.close();
      }
      return;
    }

    // Remove on the gift line, × on the picker: no gift for 24 hours on this device.
    if (event.target.closest('[data-free-gift-remove], [data-free-gift-decline]')) {
      event.preventDefault();
      declineGift();
      return;
    }

    if (event.target.closest('[data-gift-undo-button]')) {
      event.preventDefault();
      undoDecline();
      return;
    }

    if (!el || !el.contains(event.target)) return;

    const next = event.target.closest('[data-gift-next]');
    if (next) {
      event.preventDefault();
      goToStep(next, 'size');
      return;
    }

    const back = event.target.closest('[data-gift-back]');
    if (back) {
      event.preventDefault();
      goToStep(back, 'gift');
      return;
    }

    const save = event.target.closest('[data-gift-save]');
    if (save) {
      event.preventDefault();
      const variantId = state.selectedVariantId;
      if (!variantId) return;

      // Saving the gift that is already in the cart just closes the picker.
      if (el.getAttribute('data-gift-variant-id') === String(variantId)) {
        state.changing = false;
        state.step = 'gift';
        setPickerVisible(false);
        return;
      }

      selectGift(variantId);
      return;
    }

    // Picking a gift or a size only selects it; Next moves on to the sizes, Save adds it.
    const option = event.target.closest('.cart-gift-picker__products [data-gift-product]');
    if (option) {
      event.preventDefault();
      selectProduct(option);
      return;
    }

    const size = event.target.closest('[data-gift-size-row] [data-gift-variant]');
    if (size) {
      event.preventDefault();
      if (size.classList.contains('is-disabled')) return;

      markActiveSize(size.closest('[data-gift-size-row]'), size);
      state.selectedVariantId = size.getAttribute('data-gift-variant');
      updateSave(size.closest('[data-gift-style-panel]'));
    }
  });

  /**
   * The cart rebuild replaces the picker with a fresh, hidden copy. Re-apply the last verdict at
   * once so it doesn't blink, then confirm against the cart.
   */
  function onCartChange() {
    const el = picker();
    if (el && !el.hasAttribute('data-gift-line-key')) state.changing = false;
    renderUndo();
    const auto = el && el.hasAttribute('data-gift-auto-variant');
    const show =
      config.active && state.overThreshold && el && !auto && !isDeclined() &&
      (!el.hasAttribute('data-gift-line-key') || state.changing);
    if (show) {
      setPickerVisible(true);
      syncPanels();
      renderHint();
    }
    scheduleEvaluation();
  }

  document.addEventListener('theme:cart:change', onCartChange);

  // Opening the cart drawer while no gift is chosen calls attention to the picker again, once the
  // drawer has slid in.
  document.addEventListener('theme:cart:open', function () {
    setTimeout(function () {
      const el = picker();
      if (el && !el.hidden && !el.hasAttribute('data-gift-line-key')) callAttention(el, 'is-calling');
    }, 400);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', evaluateCart);
  } else {
    evaluateCart();
  }
})();
